'use client';

import { useCallback, useEffect, useState } from 'react';
import { gatewayClient } from '../api';
import { useAuth } from '../context/AuthContext';
import { ProductUpdateSubscriptionView, productUpdateKey } from '../types/productUpdates';

// A guest's memory of the products and amounts they pressed "Get updates"
// for: a per-browser convenience for the bell, never the truth.
const STORAGE_KEY = 'aceb_product_updates_v1';

function readGuestKeys(): Set<string> {
  if (typeof window === 'undefined') return new Set();
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '[]');
    return new Set(Array.isArray(parsed) ? parsed.filter((key): key is string => typeof key === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeGuestKeys(keys: Set<string>): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify([...keys]));
  } catch {
    // Storage full or disabled: the bell just forgets.
  }
}

/**
 * What the product page knows about the customer's updates for one card:
 * the account's live rows when signed in, the guest memory otherwise.
 */
export function useProductUpdates(productId: string) {
  const { user, isLoading: authLoading } = useAuth();
  const [rows, setRows] = useState<ProductUpdateSubscriptionView[]>([]);
  const [guestKeys, setGuestKeys] = useState<Set<string>>(new Set());

  const refresh = useCallback(async () => {
    if (!user) {
      setRows([]);
      setGuestKeys(readGuestKeys());
      return;
    }
    try {
      const all = await gatewayClient.cards.getAccountUpdates();
      setRows(all.filter((row) => row.productId === productId));
    } catch {
      setRows([]);
    }
  }, [user, productId]);

  useEffect(() => {
    if (authLoading) return;
    refresh();
  }, [authLoading, refresh]);

  const following = user
    ? rows.length > 0
    : [...guestKeys].some((key) => key.startsWith(`${productId}:`));

  const noteSubscribed = useCallback(
    (sku?: string) => {
      if (user) {
        refresh();
        return;
      }
      const next = new Set(readGuestKeys());
      next.add(productUpdateKey(productId, sku));
      writeGuestKeys(next);
      setGuestKeys(next);
    },
    [user, productId, refresh],
  );

  const noteStopped = useCallback(() => {
    if (user) refresh();
  }, [user, refresh]);

  return { user, rows, following, noteSubscribed, noteStopped };
}
